import { useState } from 'react'

import { portalToday } from '../shared/portalToday'
import { tierAssignments } from '../shared/homeworkTiers'
import PortalSectionDegradedBanner from '../shared/PortalSectionDegradedBanner'
import { assignmentChipClass, assignmentStatus, assignmentStatusLabel, classroomCheckFor, feedbackWindow, formatDate, formatDateWithYear } from './ParentLayout.helpers'

// Homework with tiers (#414 rider amendment 6): actionable work + the newest
// finished items by default, finished current-semester work behind "Show
// earlier homework", previous semesters behind "Show earlier semesters".
// Rendered by BOTH portals — the student portal passes renderAssignmentAction
// (the upload widget) through, so every revealed row keeps the full
// affordances. Extracted from ParentLayout.jsx (file-size split); ParentLayout
// re-exports it, so importers are unchanged.
export default function HomeworkSection({ homework, progress, t, renderAssignmentAction }) {
  const loadError = homework?.error
  const rawAssignments = homework?.data || homework || []
  // #597: attach the teacher's classroom check (feedback homework score at
  // the due session) so tiering, the chip, and the action slot all see it.
  const fbWindow = feedbackWindow(progress)
  const assignments = rawAssignments.map(a => {
    const check = classroomCheckFor(a, fbWindow.entries)
    return check ? { ...a, classroom_check: check } : a
  })
  const today = portalToday()
  const [expandedId, setExpandedId] = useState(null)
  const [showEarlierHomework, setShowEarlierHomework] = useState(false)
  const [showPreviousSemesters, setShowPreviousSemesters] = useState(false)
  const toggleExpanded = (assignmentId) => {
    setExpandedId(current => (current === assignmentId ? null : assignmentId))
  }
  const tiers = tierAssignments(assignments)
  const previousIds = new Set(tiers.previous.map(a => a.id))

  const renderRow = (a) => {
    const derived = assignmentStatus(a, today, fbWindow)
    const statusKey = derived.key
    // Effective status for the action slot: a teacher verdict (checked, or
    // the 2026-09 explicit not-submitted) settles the row — no upload owed —
    // otherwise the raw upload status.
    const status = statusKey === 'checkedInClass' ? 'checked_in_class'
      : statusKey === 'notSubmitted' ? 'not_submitted_in_class'
      : (a.submission?.status || 'not_submitted')
    // "Late" is a claim; never make it on a row whose feedback could not be loaded.
    // Operator decision 2026-09-03: neither an unknown nor a Not-recorded row
    // carries the Late badge — nobody uploads, so "late" would be a claim
    // about the teacher's bookkeeping, not the student. Late remains for the
    // upload flow's own `returned`/legacy states only. A teacher's explicit
    // Not Submitted verdict is already the red chip; no second badge.
    const isOverdue = a.due_date && a.due_date < today && status === 'not_submitted' && statusKey !== 'homeworkUnknown' && statusKey !== 'homeworkNotRecorded'
    const chipClass = assignmentChipClass(statusKey, derived.score)
    const isExpanded = expandedId === a.id
    // Previous-semester rows span years, so their dates carry the year.
    const dueLabel = previousIds.has(a.id)
      ? formatDateWithYear(a.due_date)
      : formatDate(a.due_date)
    return (
      <div
        key={a.id}
        className={`homework-row homework-row-clickable ${isExpanded ? 'homework-row-expanded' : ''}`}
        onClick={() => toggleExpanded(a.id)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            toggleExpanded(a.id)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={`${a.title} ${isExpanded ? t('hideDetails') : t('viewDetails')}`}
      >
        <div className="homework-row-top">
          <span className="homework-row-title">{a.title}</span>
          <span className={`submission-status ${chipClass}`}>
            {assignmentStatusLabel(derived, t)}
          </span>
        </div>
        <div className="homework-row-meta">
          {a.due_date && (
            <span className={isOverdue ? 'assignment-due overdue' : 'assignment-due'}>
              {t('dueDate')}: {dueLabel}
            </span>
          )}
          {isOverdue && <span className="late-badge">{t('lateSubmission')}</span>}
          <span className="homework-row-affordance" aria-hidden="true">
            <span>{isExpanded ? t('hideDetails') : t('viewDetails')}</span>
            <span className={`homework-row-chevron ${isExpanded ? 'expanded' : ''}`}>⌄</span>
          </span>
        </div>

        {isExpanded && (
          <div className="homework-details" onClick={e => e.stopPropagation()}>
            {a.description && (
              <div className="homework-detail-row">
                <span className="homework-detail-label">{t('description')}</span>
                <p className="homework-detail-text">{a.description}</p>
              </div>
            )}
            {status !== 'not_submitted' && status !== 'checked_in_class' && status !== 'not_submitted_in_class' && (
              <div className="homework-detail-row">
                <span className="homework-detail-label">{t('submittedOn')}</span>
                <span className="homework-detail-text">
                  {a.file_name && <span>{a.file_name}</span>}
                  {a.submitted_at && <span> &middot; {formatDate(a.submitted_at)}</span>}
                </span>
              </div>
            )}
            {status === 'graded' && a.grade_summary && (
              <div className="homework-detail-row">
                <span className="homework-detail-label">{t('grade')}</span>
                <div className="grade-display">{a.grade_summary}</div>
              </div>
            )}
            {a.teacher_notes && (
              <div className="homework-detail-row">
                <span className="homework-detail-label">{t('teacherNotes')}</span>
                <p className="homework-detail-text">{a.teacher_notes}</p>
              </div>
            )}
            {renderAssignmentAction?.({ assignment: a, status })}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="portal-section">
      <div className="portal-section-card portal-card-orange">
        <div className="portal-section-header">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e76f51" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/></svg>
          {t('homework')}
        </div>
        <PortalSectionDegradedBanner error={loadError} t={t} />
        {/* A failed read is UNKNOWN, never empty (repo rule; ProgressSection
            already gates its empty state the same way). Rendering the banner
            AND "No assignments yet" told a parent their child has no homework
            when the truth is that we could not find out. Rows we DID get are
            still rendered alongside the banner — only the reassuring
            empty-state copy is suppressed. */}
        {loadError ? null : assignments.length === 0 ? (
          <div className="portal-section-empty">{t('noAssignments')}</div>
        ) : (
          <>
            {tiers.recent.map(renderRow)}
            {showEarlierHomework && tiers.earlier.map(renderRow)}
            {tiers.earlier.length > 0 && (
              <button
                type="button"
                className="collapsible-toggle homework-earlier-toggle"
                aria-expanded={showEarlierHomework}
                onClick={() => setShowEarlierHomework(prev => !prev)}
              >
                {showEarlierHomework
                  ? t('hideEarlierHomework')
                  : `${t('showEarlierHomework')} (${tiers.earlier.length})`}
              </button>
            )}
            {showPreviousSemesters && tiers.previous.map(renderRow)}
            {tiers.previous.length > 0 && (
              <button
                type="button"
                className="collapsible-toggle homework-previous-toggle"
                aria-expanded={showPreviousSemesters}
                onClick={() => setShowPreviousSemesters(prev => !prev)}
              >
                {showPreviousSemesters
                  ? t('hideEarlierSemesters')
                  : `${t('showEarlierSemesters')} (${tiers.previous.length})`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
